import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Modal, Button, message } from 'antd'
import api from '../../../services/api'



const CREDIT_OPTIONS_BASE = [
  {
    value: 500,
    description: 'Starter pack for light usage.',
    color: '#52c41a',   // green
    bg: '#f6ffed'
  },
  {
    value: 1000,
    description: 'Most popular choice.',
    color: '#1890ff',   // blue
    bg: '#e8f4ff'
  },
  {
    value: 10000,
    description: 'Great for heavy usage.',
    color: '#722ed1',   // purple
    bg: '#f9f0ff'
  }
]

const PurchaseCreditsModal = ({ visible, onCancel, onSuccess, currentBalance, creditValue }) => {
  const [loading, setLoading] = useState(false)
  const [selectedCredits, setSelectedCredits] = useState(1000)
  const [customAmount, setCustomAmount] = useState('')

  const unitPrice = creditValue !== undefined ? Number(creditValue) : null;

  const CREDIT_OPTIONS = CREDIT_OPTIONS_BASE.map(opt => ({
    ...opt,
    price: unitPrice ? Math.round(opt.value * unitPrice) : null
  }))

  const customCredits = (customAmount && unitPrice) ? Math.floor(parseInt(customAmount, 10) / unitPrice) : 0
  const isCustomSelected = selectedCredits === 'custom'

  const handleCancel = () => {
    setCustomAmount('')
    setSelectedCredits(1000)
    if (onCancel) onCancel()
  }

  const handlePurchase = async () => {
    setLoading(true)
    try {
      let product_data
      if (isCustomSelected) {
        const usd = parseInt(customAmount, 10)
        if (!usd || usd <= 0) {
          message.warning('Please enter a valid USD amount.')
          setLoading(false)
          return
        }
        product_data = {
          name: `${customCredits.toLocaleString()} Credits (Custom)`,
          unit_amount: usd,
          credits: customCredits
        }
      } else {
        const option = CREDIT_OPTIONS.find(opt => opt.value === selectedCredits)
        product_data = {
          name: `${option.value.toLocaleString()} Credits`,
          unit_amount: option.price,
          credits: option.value
        }
      }

      const res = await api.aiCredits.purchaseAiCredits({ product_data })
      if (res && res.data && res.data.status !== 'error') {
        if (res.data.url) {
          window.location.href = res.data.url
        } else {
          if (onSuccess) onSuccess()
        }
      } else {
        throw new Error((res && res.data && res.data.message) || 'Failed to purchase credits')
      }
    } catch (error) {
      message.error(error.message || 'An error occurred while purchasing credits.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={<span style={{ fontWeight: 700, fontSize: 16 }}>Purchase More Credits</span>}
      visible={visible}
      onCancel={handleCancel}
      width={500}
      footer={[
        <Button key="cancel" shape="round" onClick={handleCancel} disabled={loading} style={{ minWidth: 100 }}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          shape="round"
          loading={loading}
          disabled={!unitPrice}
          onClick={handlePurchase}
          style={{ minWidth: 160 }}
        >
          Proceed to Payment
        </Button>
      ]}
    >
      <div style={{ padding: '4px 0 8px' }}>

        {/* Current Balance */}
        {currentBalance != null && (
          <div style={{
            background: '#f5f7fa',
            borderRadius: 8,
            padding: '10px 16px',
            marginBottom: 20,
            fontSize: 14,
            color: '#555',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              minWidth: 0 
            }}>
              <span style={{ whiteSpace: 'nowrap' }}>Current Balance:&nbsp;</span>
              <strong 
                style={{ 
                  color: '#222', 
                  whiteSpace: 'nowrap', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis' 
                }} 
                title={`${currentBalance.toLocaleString()} Credits`}
              >
                {currentBalance.toLocaleString()} Credits
              </strong>
            </div>
            {creditValue !== undefined && (
              <div style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                Credit Price:&nbsp;
                <strong style={{ color: '#222' }}>
                  ${Number(creditValue).toFixed(2)} / credit
                </strong>
              </div>
            )}
          </div>
        )}

        {!unitPrice ? (
          <div style={{
            padding: '30px 20px',
            background: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: 8,
            textAlign: 'center',
            marginTop: 16
          }}>
            <div style={{ color: '#cf1322', fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
              Credit Value Not Set
            </div>
            <div style={{ color: '#cf1322', fontSize: 14 }}>
              Your credit value must be set before any purchase can be made, please contact <a
                style={{ justifyContent: 'center' }}
                href="mailto:admin@sharelook.com"
              >
                admin@sharelook.com
              </a>
            </div>
          </div>
        ) : (
          <>
            {/* Credit Package Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {CREDIT_OPTIONS.map(option => {
                const isSelected = selectedCredits === option.value

                return (
                  <div
                    key={option.value}
                    onClick={() => setSelectedCredits(option.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 18px',
                      border: `2px solid ${isSelected ? '#1890ff' : '#e0e0e0'}`,
                      borderRadius: 10,
                      cursor: 'pointer',
                      background: isSelected ? '#e6f4ff' : '#fff',
                      transition: 'all 0.2s ease',
                      boxShadow: isSelected ? '0 0 0 1px #1890ff22' : 'none'
                    }}
                  >
                    {/* Left: credits + description */}
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#222' }}>
                        {option.value.toLocaleString()}&nbsp;
                        <span style={{ fontWeight: 400, fontSize: 14, color: '#555' }}>Credits</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                        {option.description}
                      </div>
                    </div>

                    {/* Right: price */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#222' }}>
                        {option.price ? `$${option.price}` : '—'}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Custom Purchase */}
            <div
              onClick={() => setSelectedCredits('custom')}
              style={{
                marginTop: 16,
                padding: '16px 18px',
                border: `2px solid ${isCustomSelected ? '#fa8c16' : '#fa8c1655'}`,
                borderRadius: 10,
                cursor: 'pointer',
                background: isCustomSelected ? '#fff7e6' : '#fff',
                transition: 'all 0.2s ease',
                boxShadow: isCustomSelected ? '0 0 0 1px #fa8c1622' : 'none'
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14, color: '#fa8c16', marginBottom: 10 }}>
                Custom Purchase
              </div>

              {/* Amount input */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: `1.5px solid ${isCustomSelected ? '#fa8c16' : '#d9d9d9'}`,
                  borderRadius: 20,
                  padding: '6px 14px',
                  background: '#fff',
                  fontSize: 14,
                  color: '#555'
                }}
                onClick={e => e.stopPropagation()}
              >
                <span style={{ marginRight: 4 }}>Amount to spend:&nbsp;<strong>$</strong></span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={customAmount}
                  placeholder="Enter Amount"
                  onFocus={() => setSelectedCredits('custom')}
                  onKeyDown={e => {
                    if (['.', '-', '+', 'e', 'E'].includes(e.key)) {
                      e.preventDefault()
                    }
                  }}
                  onChange={e => {
                    const val = e.target.value.replace(/[^0-9]/g, '')
                    setCustomAmount(val)
                    setSelectedCredits('custom')
                  }}
                  style={{
                    border: 'none',
                    outline: 'none',
                    flex: 1,
                    fontSize: 14,
                    color: '#222',
                    background: 'transparent'
                  }}
                />
              </div>

              {/* Estimated credits */}
              <div style={{ marginTop: 10, fontSize: 13, color: '#555' }}>
                Estimated Credits:&nbsp;
                <strong style={{ fontSize: 22, color: '#fa8c16' }}>
                  {customCredits > 0 ? customCredits.toLocaleString() : '—'}
                </strong>
                {customCredits > 0 && <span style={{ fontSize: 13, color: '#555' }}> Credits</span>}
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                  {unitPrice ? `(Based on $${unitPrice.toFixed(2)} per credit)` : '(Unit price unavailable)'}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Tax note */}
        {/* <p style={{ marginTop: 16, marginBottom: 0, fontSize: 12, color: '#aaa' }}>
          All prices exclude taxes.
        </p> */}
      </div>
    </Modal>
  )
}

export default PurchaseCreditsModal
